import { useCallback, useEffect, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlagExposed } from '@zenbusiness/application-commons-experiments';

import { VELO_ADD_NEW_BUSINESS_FORMATION_CHECKOUT_EXPERIMENT } from '#client/constants/experimentsFlags';
import * as ROUTES from '#client/constants/routes';

// How long a click waits for the flag before falling back to the control experience
export const FLAG_LOADING_TIMEOUT_MS = 2000;

const CONTROL_TOPIC = 'start-new-llc';
const VARIANT_TOPIC = 'add-new-business';

/**
 * Link and click handler for the "Add new business" / "Form a new business" menu item.
 * Clicking "Add new business" sends the customer to Velo with the topic for their formation
 * checkout experiment variation; any other click follows the link.
 *
 * @param {boolean} hasBusinessEntities - Whether the customer already has a business entity
 * @returns {{ newCompanyLink: string, onNewCompanyClick: (event: Event) => void }}
 */
export default function useNewCompanyLink(hasBusinessEntities) {
  const { businessEntityUuid } = useBusinessEntity();
  const veloCompanyLink = businessEntityUuid
    ? ROUTES.VELO_GUIDE_COMPANY.replace('[businessEntityUuid]', businessEntityUuid)
    : null;
  const newCompanyLink = veloCompanyLink ? `${veloCompanyLink}?topic=${CONTROL_TOPIC}` : ROUTES.VELO_GUIDE;

  // The flag is evaluated only after the click (skipEvaluation = !isClicked), so only customers who
  // click are exposed. The null default makes the value change on click, which is what fires the
  // exposure event. withLoadingFlag = true lets the redirect wait until the flag has loaded.
  const [isClicked, setIsClicked] = useState(false);
  const { value: variation, loading } = useExperimentFlagExposed(
    VELO_ADD_NEW_BUSINESS_FORMATION_CHECKOUT_EXPERIMENT,
    null,
    true,
    !isClicked,
  );

  useEffect(() => {
    if (!isClicked) {
      return undefined;
    }

    // Reset so the link works again if the browser restores this page from its back/forward cache
    const redirect = (topic) => {
      setIsClicked(false);
      window.location.assign(`${veloCompanyLink}?topic=${topic}`);
    };

    if (!loading) {
      redirect(variation === 'variant' ? VARIANT_TOPIC : CONTROL_TOPIC);
      return undefined;
    }

    const timeout = setTimeout(() => redirect(CONTROL_TOPIC), FLAG_LOADING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isClicked, loading, variation, veloCompanyLink]);

  const onNewCompanyClick = useCallback(
    (event) => {
      const isNewTabOrWindow = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

      if (!veloCompanyLink || !hasBusinessEntities || isNewTabOrWindow) {
        return;
      }

      event.preventDefault();
      setIsClicked(true);
    },
    [hasBusinessEntities, veloCompanyLink],
  );

  return { newCompanyLink, onNewCompanyClick };
}
